import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles.module.scss';

// Types
import { FormData, ProductType, ConsentState, SingleAnswerSlide, FormInputSlide } from './types';

// Hooks
import { useSliderNavigation, useZipCodeCheck, useSavingsCalculator } from './hooks';

// Components
import ProgressBar from './components/ProgressBar';
import SlideHeader from './components/SlideHeader';
import ChoiceGrid from './components/ChoiceGrid';
import FormInput from './components/FormInput';
import SuccessSlide from './components/SuccessSlide';
import {
  DEFAULT_OPTIONAL_CONSENTS,
  OptionalConsentAnswers,
} from '../../shared/consent/OptionalConsents';

// Utils
import { submitLead, updateConsent } from './utils/api';
import {
  trackSlideAnswer,
  trackSliderSuccess,
  trackSliderFailure,
  trackLeadCreated,
  trackEnpalSourceCookie,
} from './utils/tracking';
import { getUTMParams, splitFullName } from './utils/helpers';

/** Slide con header beneficio senza riga cumulativa "Risparmio totale" nell'header */
const SLIDE_IDS_WITHOUT_HEADER_TOTAL: ReadonlySet<string> = new Set([
  'decision_stage',
  'name_form',
  'email_form',
  'phone_form',
]);

/**
 * SliderBlu — copia di slider_privacy_push, con bonus CAP dinamici (short_cap) e banner CAP (all_bonus).
 */
const SliderBlu: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({});
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  const [mongoId, setMongoId] = useState<string>('');
  const [leadUid, setLeadUid] = useState<string>('');

  const [optionalConsents, setOptionalConsents] = useState<OptionalConsentAnswers>(
    DEFAULT_OPTIONAL_CONSENTS
  );

  const consentForApi: ConsentState = useMemo(
    () => ({
      enpalMarketingConsent: optionalConsents.enpalMarketingConsent === true,
      thirdPartyMarketing: optionalConsents.thirdPartyMarketing === true,
      thirdPartyProfiling: optionalConsents.thirdPartyProfiling === true,
    }),
    [optionalConsents]
  );

  const {
    currentSlide,
    progress,
    goNext,
    goPrev,
    goToSlideById,
    canGoPrev,
  } = useSliderNavigation(formData);

  const { zipFlag, isValid: isCapValid, capFeedback, checkZip } = useZipCodeCheck();

  const { tooltipText, getBenefitText, getTotalSavings, getNextButtonText, getBonusCountText } =
    useSavingsCalculator(formData.product_type as ProductType, zipFlag, formData.zip);

  useEffect(() => {
    const utmParams = getUTMParams();
    trackEnpalSourceCookie(utmParams.utm_source || 'organic');
  }, []);

  const updateFormData = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleAnswerSelect = useCallback(
    (slide: SingleAnswerSlide, value: string) => {
      updateFormData(slide.apiFieldName, value);
      trackSlideAnswer(slide.id, slide.title || '', value);

      if (slide.autoAdvance) {
        setTimeout(() => goNext(), 200);
      }
    },
    [updateFormData, goNext]
  );

  const handleFormInputChange = useCallback(
    (name: string, value: string) => {
      setFormValues((prev) => ({ ...prev, [name]: value }));

      if (name === 'zip') {
        checkZip(value, formData.product_type as ProductType);
        updateFormData('zip', value);
      }
    },
    [checkZip, updateFormData, formData.product_type]
  );

  const handleLeadSubmit = useCallback(async (consent: ConsentState) => {
    goToSlideById('loading');

    const uid = uuidv4();
    setLeadUid(uid);

    const finalFormData: FormData = {
      ...formData,
      ...formValues,
    };

    try {
      const id = await submitLead(finalFormData, uid, consent);

      if (id) {
        setMongoId(id);
        trackSliderSuccess();

        if (finalFormData.email) {
          const { firstName, lastName } = splitFullName(finalFormData.name || '');
          setTimeout(() => {
            trackLeadCreated(
              finalFormData.email!,
              finalFormData.phone || '',
              finalFormData.zip || '',
              firstName,
              lastName
            );
          }, 120);
        }

        goToSlideById('success');
      } else {
        trackSliderFailure();
        goToSlideById('error');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      trackSliderFailure();
      goToSlideById('error');
    }
  }, [formData, formValues, goToSlideById]);

  const handleFormSubmit = useCallback(
    async (slide: FormInputSlide, consent?: ConsentState) => {
      Object.entries(formValues).forEach(([key, value]) => {
        updateFormData(key, value);
      });

      trackSlideAnswer(slide.id, slide.title || '', 'continue_button');

      if (slide.id === 'phone_form') {
        await handleLeadSubmit(consent ?? consentForApi);
      } else {
        goNext();
      }
    },
    [formValues, updateFormData, goNext, handleLeadSubmit, consentForApi]
  );


  const handleConsentsUpdate = useCallback(
    (next: ConsentState) => {
      setOptionalConsents({
        enpalMarketingConsent: next.enpalMarketingConsent,
        thirdPartyMarketing: next.thirdPartyMarketing,
        thirdPartyProfiling: next.thirdPartyProfiling,
      });
      if (leadUid) {
        updateConsent(leadUid, next);
      }
    },
    [leadUid]
  );

  const handleRetry = useCallback(() => {
    goToSlideById('customer_type');
  }, [goToSlideById]);

  const getSubmitLabel = (slideId: string): string => {
    if (slideId === 'phone_form') {
      return 'Scopri il risparmio totale';
    }
    if (slideId === 'name_form' || slideId === 'email_form') {
      return getNextButtonText(slideId);
    }
    return 'Avanti';
  };

  const renderSlideContent = () => {
    if (!currentSlide) return null;

    switch (currentSlide.type) {
      case 'SINGLE_ANSWER': {
        const slide = currentSlide as SingleAnswerSlide;
        const benefitText = getBenefitText(slide.id);
        const bonusCountText = getBonusCountText(slide.id);

        return (
          <>
            {slide.header && (
              <SlideHeader
                savingsSubText={bonusCountText || slide.header.savingsSubText}
                savingsText={benefitText || slide.header.savingsText}
                tooltipText={tooltipText}
                zipCode={formData.zip}
                totalSavings={
                  SLIDE_IDS_WITHOUT_HEADER_TOTAL.has(slide.id)
                    ? undefined
                    : getTotalSavings(slide.id)
                }
              />
            )}

            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{slide.title}</h3>

              <ChoiceGrid
                answers={slide.answers}
                selectedValue={formData[slide.apiFieldName]}
                onSelect={(value) => handleAnswerSelect(slide, value)}
                columns={slide.id === 'install_timeline' || slide.id === 'decision_stage' ? 2 : 3}
              />

              {!slide.autoAdvance && (
                <div className={styles.buttonGroup}>
                  {canGoPrev && (
                    <button type="button" className={styles.btnPrev} onClick={goPrev}>
                      Indietro
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.btnNext}
                    onClick={goNext}
                    disabled={!formData[slide.apiFieldName]}
                  >
                    Avanti
                  </button>
                </div>
              )}
            </div>
          </>
        );
      }

      case 'FORM_INPUT': {
        const slide = currentSlide as FormInputSlide;
        const benefitText = getBenefitText(slide.id);
        const bonusCountText = getBonusCountText(slide.id);
        const isPhoneSlide = slide.id === 'phone_form';
        const isLocationSlide = slide.id === 'location_form';

        return (
          <>
            {slide.header && (
              <SlideHeader
                savingsSubText={bonusCountText || slide.header.savingsSubText}
                savingsText={benefitText || slide.header.savingsText}
                tooltipText={tooltipText}
                zipCode={formData.zip}
                totalSavings={
                  SLIDE_IDS_WITHOUT_HEADER_TOTAL.has(slide.id)
                    ? undefined
                    : getTotalSavings(slide.id)
                }
              />
            )}

            <div className={styles.slideBody}>
              {slide.title && <h3 className={styles.wizardTitle}>{slide.title}</h3>}

              <FormInput
                inputs={slide.inputs}
                values={formValues}
                onChange={handleFormInputChange}
                onSubmit={() => handleFormSubmit(slide)}
                submitLabel={getSubmitLabel(slide.id)}
                showBackButton={canGoPrev && !isPhoneSlide}
                onBack={goPrev}
                isPhoneSlide={isPhoneSlide}
                isLocationSlide={isLocationSlide}
                capFeedback={capFeedback}
                isCapValid={isCapValid}
                optionalConsents={isPhoneSlide ? optionalConsents : undefined}
                onOptionalConsentsChange={
                  isPhoneSlide ? setOptionalConsents : undefined
                }
                onPhoneContinue={
                  isPhoneSlide
                    ? (answers) => {
                        setOptionalConsents(answers);
                        handleFormSubmit(slide, {
                          enpalMarketingConsent: answers.enpalMarketingConsent === true,
                          thirdPartyMarketing: answers.thirdPartyMarketing === true,
                          thirdPartyProfiling: answers.thirdPartyProfiling === true,
                        });
                      }
                    : undefined
                }
              />
            </div>
          </>
        );
      }

      case 'INFO': {
        if (currentSlide.id === 'loading') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Attendere prego...</p>
              <img
                src="https://i.gifer.com/origin/34/34338d26023e5515f6cc8969aa027bca.gif"
                alt="Loading..."
                style={{ marginTop: '1rem', maxWidth: '80px', borderRadius: '10px' }}
              />
            </div>
          );
        }

        if (currentSlide.id === 'success') {
          return (
            <SuccessSlide
              productType={(formData.product_type || 'Photovoltaic') as ProductType}
              zipFlag={zipFlag}
              zipCode={formData.zip}
              mongoId={mongoId}
              phone={formData.phone}
              consents={consentForApi}
              onConsentsChange={handleConsentsUpdate}
            />
          );
        }

        if (currentSlide.id === 'error') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Si è verificato un errore...</p>
              <button
                type="button"
                className={styles.btnNext}
                onClick={handleRetry}
                style={{ marginTop: '1rem', maxWidth: '200px' }}
              >
                Riprova
              </button>
            </div>
          );
        }

        return (
          <div className={styles.slideBody}>
            {currentSlide.title && <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>}
            {currentSlide.html && (
              <div dangerouslySetInnerHTML={{ __html: currentSlide.html }} />
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <section className={styles.sliderSection}>
      <header className={styles.pageHeader}>
        <EnpalLogo className={styles.brandLogo} />
        <div className={styles.googleRating}>
          <span className={styles.googleStars} aria-hidden="true">
            {Array.from({ length: 5 }, (_, index) => (
              <GoogleStar key={index} className={styles.googleStar} />
            ))}
          </span>
          <span className={styles.googleCaption}>4,8 stelle su Google</span>
        </div>
      </header>
      <div className={styles.sliderStage}>
        <div className={styles.sliderContainer}>
          <div className={styles.sliderWrapper}>
            <ProgressBar progress={progress} />

            <div className={styles.wizSlide}>
              {renderSlideContent()}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

function EnpalLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 262.8 76.5" role="img" aria-label="Enpal">
      <path fill="#072543" d="M0,2.6h40.8v10.7h-27v11.6h23.8v10.8H13.8v11.6h27V58H0V2.6z" />
      <path fill="#072543" d="M47.9,17.2h12.9l0.4,3.5c1.8-1.3,3.9-2.3,6.5-3.1c2.6-0.8,5.2-1.2,7.9-1.2c5.2,0,9,1.3,11.5,3.8c2.4,2.5,3.7,6.4,3.7,11.7V58H76.9v-25c0-2.3-0.5-3.9-1.5-4.9c-1-1-2.7-1.5-5.2-1.5c-1.5,0-3,0.3-4.6,1c-1.5,0.7-2.8,1.5-3.9,2.6V58H47.9V17.2z" />
      <path fill="#072543" d="M99.5,17.2h12l0.7,3.5c3.6-2.9,7.8-4.4,12.8-4.4c12.6,0,18.8,6.8,18.8,20.4c0,7.2-1.7,12.7-5.2,16.5c-3.4,3.8-8.2,5.6-14.4,5.6c-4.3,0-8-0.9-11-2.7v20.4H99.5V17.2z M127.5,45.6c1.5-2.1,2.3-5.1,2.3-9.1c0-3.8-0.7-6.4-2-7.8c-1.3-1.4-3.6-2.1-6.7-2.1c-2.9,0-5.5,1-7.7,3.1v16.5c1.1,0.9,2.3,1.5,3.4,1.9c1.1,0.4,2.5,0.6,4.1,0.6C123.8,48.7,126,47.6,127.5,45.6z" />
      <path fill="#072543" d="M153.7,55.5c-2.7-2.2-4.1-5.4-4.1-9.5c0-4.2,1.4-7.5,4.3-9.9c2.9-2.4,7.1-3.5,12.7-3.5H178v-0.9c0-2.1-0.7-3.6-2.1-4.5c-1.4-0.9-4-1.3-7.7-1.3c-4.6,0-9.2,0.7-13.7,2.2v-8.9c2.1-0.8,4.6-1.5,7.5-2c2.9-0.5,5.9-0.8,9-0.8c6.5,0,11.5,1.3,15,4c3.5,2.6,5.2,6.7,5.2,12.2V58h-12.1l-0.7-3.4c-3.2,2.9-7.9,4.3-14.4,4.3C159.9,58.8,156.4,57.7,153.7,55.5z M178,46.4v-5.8h-9.8c-2.1,0-3.6,0.4-4.6,1.2c-1,0.8-1.5,2-1.5,3.7c0,3.2,2.1,4.8,6.4,4.8C172.6,50.3,175.8,49,178,46.4z" />
      <path fill="#072543" d="M203.2,55.9c-1.9-1.9-2.9-5.1-2.9-9.4V0h13.8v45.1c0,1.3,0.3,2.2,0.8,2.7c0.5,0.5,1.3,0.7,2.5,0.7c1.5,0,2.9-0.2,4.1-0.6v9.4c-1.4,0.5-2.7,0.9-4,1.1c-1.3,0.2-2.9,0.3-4.7,0.3C208.4,58.8,205.2,57.8,203.2,55.9z" />
      <circle fill="#FFB000" cx="252.3" cy="50" r="10.5" />
    </svg>
  );
}

function GoogleStar({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#FBBC04"
        d="M12 2.6l2.7 5.6 6.2.9-4.5 4.3 1.1 6.1L12 16.8 6.5 19.5l1.1-6.1L3.1 9.1l6.2-.9L12 2.6z"
      />
    </svg>
  );
}

export default SliderBlu;
